import {Authenticator, Button, Heading, Image, useAuthenticator, useTheme, View} from '@aws-amplify/ui-react';
import type {ResourcesConfig} from 'aws-amplify';
import {Amplify} from 'aws-amplify';
import {useTenantContext} from '../../tenant/TenantContext';
import {useNavigate} from 'react-router-dom';
import {confirmResetPassword, fetchAuthSession, resetPassword, signIn, signOut} from '@aws-amplify/auth';
import {useEffect} from 'react';
import {setupAwsAmplifyI18N} from '../../helpers/setupAwsAmplifyI18N';
import {LoginFooterUI} from '../../common/ui/LogInFooterUI';

const authConfig: ResourcesConfig['Auth'] = {
    Cognito: {
        userPoolId: process.env.REACT_APP_USER_POOL_ID,
        userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
        loginWith: {
            username: true,
            email: false,
            phone: false
        }
    }
};

Amplify.configure({
    Auth: authConfig
});

// TODO: needed because amplify form buttons' text cannot be changed
setupAwsAmplifyI18N();

const buildUsername = (userCode, tenantId) => `${userCode}-${tenantId}`;

// Cognito devuelve casi todo como NotAuthorizedException; el texto del mensaje es lo que distingue
// una temporal caducada de una contraseña mal escrita. Aquí se traduce a algo que el cliente entienda.
const SOLICITAR = 'Puedes solicitar credenciales nuevas con el enlace de abajo.';
const signInErrorMessage = (error) => {
    const message = error?.message ?? '';
    if (/Temporary password has expired/i.test(message)) {
        return `Tu contraseña temporal ha caducado. ${SOLICITAR}`;
    }
    if (/Password attempts exceeded/i.test(message)) {
        return 'Demasiados intentos fallidos. Espera unos minutos antes de volver a intentarlo.';
    }
    if (error?.name === 'NotAuthorizedException' || error?.name === 'UserNotFoundException') {
        return 'Código de cliente o contraseña incorrectos.';
    }
    if (error?.name === 'UserNotConfirmedException') {
        return `Tu usuario todavía no está activado. ${SOLICITAR}`;
    }
    if (error?.name === 'PasswordResetRequiredException') {
        return 'Tienes que restablecer tu contraseña: usa "¿Olvidó su contraseña?".';
    }
    return 'Ha ocurrido un error inesperado, por favor contacte con nosotros.';
};

const forgotPasswordErrorMessage = (error) => {
    const message = error?.message ?? '';
    if (error?.name === 'LimitExceededException') {
        return 'Se ha excedido el límite de intentos, inténtelo después de un tiempo.';
    }
    if (/cannot be reset in the current state/i.test(message) || error?.name === 'NotAuthorizedException') {
        return `No se puede recuperar la contraseña porque tu usuario aún no ha completado el primer acceso. ${SOLICITAR}`;
    }
    if (error?.name === 'UserNotFoundException') {
        return 'No existe ningún usuario con ese código de cliente.';
    }
    if (error?.name === 'InvalidParameterException') {
        return `Tu usuario no tiene un email verificado para enviarte el código. ${SOLICITAR}`;
    }
    if (error?.name === 'CodeMismatchException') {
        return 'El código no es correcto. Revisa el correo y vuelve a intentarlo.';
    }
    if (error?.name === 'ExpiredCodeException') {
        return 'El código ha caducado. Vuelve a pedir uno nuevo desde "¿Olvidó su contraseña?".';
    }
    if (error?.name === 'InvalidPasswordException') {
        return 'La contraseña debe tener al menos 8 caracteres, con mayúscula, minúscula y número.';
    }
    return 'No se ha podido completar la operación. Por favor, inténtalo de nuevo o contacta con nosotros.';
};

export const AwsCustomAuthenticator = ({children}) => {
    const {user} = useAuthenticator(context => [context.user]);
    const navigate = useNavigate();
    const {tenantId} = useTenantContext();

    useEffect(() => {
        if (user) {
            navigate('/');
        }
    }, [navigate, user]);

    return (
        <Authenticator hideSignUp={true}
                       components={{
                           Header() {
                               const {tokens} = useTheme();
                               const {businessName, logo} = useTenantContext();

                               return (
                                   <View textAlign="center" padding={tokens.space.large}>
                                       <Image alt={businessName} src={logo}/>
                                   </View>
                               );
                           },
                           ConfirmResetPassword: {
                               Footer() {
                                   const {toSignIn} = useAuthenticator();
                                   return (
                                       <Button size="small" variation="link" onClick={toSignIn}>
                                           Volver a inicio de sesión
                                       </Button>
                                   );
                               }
                           },
                           SignIn: {
                               Header() {
                                   const {tokens} = useTheme();

                                   return (
                                       <Heading
                                           textAlign="center"
                                           padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                                           level={3}
                                       >
                                           Pedidos Online
                                       </Heading>
                                   );
                               },
                               Footer() {
                                   const {toForgotPassword} = useAuthenticator();
                                   return <LoginFooterUI onForgotPassword={toForgotPassword} /> ;
                               }
                           }
                       }}
                       formFields={{
                           signIn: {
                               username: {
                                   label: null,
                                   placeholder: 'Código de cliente',
                                   isRequired: true,
                                   autocomplete: 'username'
                               },
                               password: {
                                   label: null,
                                   placeholder: 'Contraseña',
                                   autocomplete: 'current-password'
                               }
                           },
                           forceNewPassword: {
                               password: {
                                   placeholder: 'Nueva contraseña',
                                   label: null
                               },
                               confirm_password: {
                                   placeholder: 'Por favor, confirme su nueva contraseña',
                                   label: null,
                                   isRequired: true,
                               }
                           },
                           forgotPassword: {
                               username: {
                                   label: null,
                                   placeholder: 'Código de cliente'
                               }
                           },
                           confirmResetPassword: {
                               confirmation_code: {
                                   placeholder: 'Código enviado a tu email',
                                   label: null
                               },
                               password: {
                                   placeholder: 'Nueva contraseña',
                                   label: null
                               },
                               confirm_password: {
                                   placeholder: 'Confirmar contraseña',
                                   label: null
                               }
                           }
                       }}
                       services={{
                           async handleSignIn(input) {
                               try {
                                   const username = buildUsername(input.username, tenantId);
                                   const signInOutput = await signIn({
                                       ...input,
                                       username
                                   });
                                   const session = await fetchAuthSession();
                                   if (session.tokens) {
                                       const userGroups = session.tokens.accessToken.payload['cognito:groups'] || [];
                                       if (userGroups.includes('admin')) {
                                           await signOut();
                                           return Promise.reject(new Error('Los usuarios administradores no tienen permitido iniciar sesión.'));
                                       }
                                       window.location.href = '/';
                                   }
                                   return signInOutput;
                               } catch (error) {
                                   console.error(error)
                                   await signOut();
                                   return Promise.reject(new Error(signInErrorMessage(error)));
                               }
                           },
                           async handleForgotPassword(input) {
                               const username = buildUsername(input.username, tenantId);
                               try {
                                   return await resetPassword({
                                       ...input,
                                       username
                                   });
                               } catch (error) {
                                   console.error(error);
                                   return Promise.reject(new Error(forgotPasswordErrorMessage(error)));
                               }
                           },
                           async handleForgotPasswordSubmit(input) {
                               const username = buildUsername(input.username, tenantId);
                               try {
                                   return await confirmResetPassword({
                                       ...input,
                                       username
                                   });
                               } catch (error) {
                                   console.error(error);
                                   return Promise.reject(new Error(forgotPasswordErrorMessage(error)));
                               }
                           }
                       }}
                       className="position-absolute top-50 start-50 translate-middle">
            {children}
        </Authenticator>
    );
};